import { useCallback, useEffect, useState } from 'react';
import { initializeDefaultUser } from '../utils/authServiceUtil';
import { loadingSpinner, getCachedLogoUrl } from '../utils/bookingUtils';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import { LOADING_TIMEOUT_MS } from '../constants/bookingConst';

/**
 * Signs the visitor in to the shared Firebase account before anything else in
 * the app renders, because the Realtime Database rules gate every read on
 * `auth != null`.
 *
 * This guard sits ABOVE ConfigProvider and the router, so while it is busy
 * none of the app's other watchdogs exist yet: BookingApp's `configTimedOut`
 * check lives in a tree that has not mounted. That is why a stuck boot used
 * to spin forever with no timeout screen — the timeout screen was downstream
 * of the thing that was stuck.
 *
 * The old shape awaited initializeDefaultUser() and cleared `loading` in a
 * `finally`. `finally` runs when a promise settles, and the two awaits inside
 * can both stay pending indefinitely: Firebase Auth stalling on a blocked
 * IndexedDB read, or a sign-in request swallowed by a captive portal or a
 * half-open connection (the Auth SDK applies no client-side timeout). So the
 * bootstrap is now raced against the same LOADING_TIMEOUT_MS every other
 * loading state uses, and a timeout renders ConnectionErrorScreen with a
 * working Try Again instead of a spinner.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - the app, rendered once signed in.
 * @returns {JSX.Element}
 */
export function AuthGuard({ children }) {
  const [user, setUser] = useState(null);
  // 'loading' -> 'ready' | 'failed'. A single status rather than separate
  // loading/error flags, so there is no combination that renders nothing.
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // `active` makes the first outcome the final one. It is what keeps a
    // stalled sign-in that finally lands 30s late from yanking the error
    // screen away after the guest has already been told to retry, and keeps
    // either path from setting state on an unmounted tree.
    let active = true;
    setStatus('loading');

    const timer = setTimeout(() => {
      if (!active) return;
      active = false;
      console.error(`App startup auth exceeded ${LOADING_TIMEOUT_MS}ms`);
      setStatus('failed');
    }, LOADING_TIMEOUT_MS);

    initializeDefaultUser()
      .then((firebaseUser) => {
        if (!active) return;
        active = false;
        clearTimeout(timer);
        setUser(firebaseUser);
        setStatus(firebaseUser ? 'ready' : 'failed');
      })
      .catch((error) => {
        if (!active) return;
        active = false;
        clearTimeout(timer);
        console.error('App startup auth failed:', error);
        setStatus('failed');
      });

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // `attempt` is a dependency the body deliberately does not read: it is
    // here only so handleRetry() re-runs the bootstrap from the top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  /**
   * Runs the whole bootstrap again from scratch. Wired to the error screen's
   * Try Again, which is the only way out of the failed state — deliberately,
   * so the guest decides when to move rather than a late response deciding
   * for them.
   */
  const handleRetry = useCallback(() => setAttempt((n) => n + 1), []);

  if (status === 'loading') {
    // Logo comes from the localStorage cache rather than config — this
    // screen renders before ConfigProvider mounts (see getCachedLogoUrl).
    return loadingSpinner('Preparing your experience', getCachedLogoUrl());
  }

  // Covers both a failed/timed-out bootstrap and the should-not-happen case
  // of a resolved bootstrap with no user: same dead end for the guest, so the
  // same screen and the same way out.
  if (status === 'failed' || !user) {
    return <ConnectionErrorScreen onRetry={handleRetry} />;
  }

  return children;
}
